/**
 * The cabinet's side panels (docs/spec/04 §3): on a wide window the 224 x 288 screen sits between
 * two 96-pixel panels, as a monitor sits in its bezel. In a game the left one is the instruction
 * card and the right one the gauges; in the menus they are sky.
 */

import type { GameState } from '../sim';
import { chainMultiplier, NOVA, SCORE } from '../sim';
import type { Colour } from './font';
import { CYAN, DARK_GREY, GREY, MAGENTA, ORANGE, RED, SKY, WHITE, YELLOW } from './palette';
import type { Screen } from './screen';
import { CAPSULE_LOOK, drawCapsule, drawSprite } from './sprites';
import type { Starfield } from './starfield';

export const PANEL_W = 96;

const BACK = '#05070f';
const EDGE = '#1b2444';
const MID = PANEL_W / 2;

/** The panel's ground and the line where it meets the screen. `inner`: which side that is. */
function ground(p: Screen, inner: 'left' | 'right'): void {
  p.clear(BACK);
  p.fill(inner === 'left' ? 0 : PANEL_W - 1, 0, 1, p.height, EDGE);
}

function title(p: Screen, text: string, row: number, colour: Colour = RED): void {
  p.text(text, MID, row * 8, colour, 'center');
}

/** Panels with nothing to say: a quiet sky behind the bezel line. */
export function drawSkyPanels(left: Screen, right: Screen, stars: Starfield, deep: boolean, calm: boolean): void {
  ground(left, 'right');
  ground(right, 'left');
  left.faded(0.45, () => stars.draw(left, deep, calm));
  right.faded(0.45, () => stars.draw(right, deep, calm));
}

export interface PanelInfo {
  /** `SUPERNOVA`, `CLASSIC 1981`, `DAILY RUN`, `DEMO`. */
  readonly modeName: string;
  /** Short key names: move left, move right, fire, Nova, pause. */
  readonly keys: { left: string; right: string; fire: string; bomb: string; pause: string };
  readonly calm: boolean;
}

/** The instruction card: what the game is, the stage, what the hive pays, the keys. */
function drawCard(p: Screen, s: GameState, info: PanelInfo): void {
  ground(p, 'right');
  title(p, info.modeName, 2, s.mode === 'supernova' ? CYAN : YELLOW);
  title(p, 'STAGE', 5);
  p.text(String(s.stage), MID, 56, WHITE, 'center', 2);
  title(p, 'THE HIVE', 11);
  const beat = Math.floor(s.tick / 32);
  const line = (sprite: 'stinger' | 'moth' | 'monarch', y: number, home: number, diving: string): void => {
    drawSprite(p, sprite, beat, 14, y + 4);
    p.text(String(home), 52, y, WHITE, 'right');
    p.text(diving, 90, y, YELLOW, 'right');
  };
  p.text('HOME', 52, 100, GREY, 'right');
  p.text('DIVE', 90, 100, GREY, 'right');
  line('stinger', 114, SCORE.stinger[0], String(SCORE.stinger[1]));
  line('moth', 134, SCORE.moth[0], String(SCORE.moth[1]));
  line('monarch', 154, SCORE.monarch[0], `${SCORE.monarch[1]}+`);
  title(p, 'KEYS', 23);
  const key = (name: string, what: string, y: number): void => {
    p.text(name.slice(0, 5), 6, y, CYAN);
    p.text(what, 90, y, WHITE, 'right');
  };
  key(`${info.keys.left} ${info.keys.right}`.length <= 5 ? `${info.keys.left} ${info.keys.right}` : '◄ ►', 'MOVE', 198);
  key(info.keys.fire, 'FIRE', 210);
  if (s.mode === 'supernova') key(info.keys.bomb, 'NOVA', 222);
  key(info.keys.pause, 'PAUSE', s.mode === 'supernova' ? 234 : 222);
}

const MULT_COLOURS: readonly Colour[] = [GREY, GREY, YELLOW, ORANGE, RED, MAGENTA];

function bar(p: Screen, y: number, part: number, colour: Colour): void {
  p.fill(8, y, 80, 2, DARK_GREY);
  p.fill(8, y, Math.round(80 * Math.max(0, Math.min(1, part))), 2, colour);
}

/** Supernova's gauges: the chain, the powers running, the Nova stock. */
function drawGauges(p: Screen, s: GameState): void {
  ground(p, 'left');
  const mult = chainMultiplier(s.chain);
  const colour = MULT_COLOURS[mult] ?? WHITE;
  title(p, 'CHAIN', 2);
  p.text(String(s.chain), MID - 4, 28, s.chain > 0 ? WHITE : DARK_GREY, 'right', 2);
  p.text(`x${mult}`, MID + 4, 28, colour, 'left', 2);
  bar(p, 50, s.chain > 0 ? s.chainClock / NOVA.chainClock : 0, colour);
  p.text(`BEST ${s.stats.bestChain}`, MID, 58, GREY, 'center');
  title(p, 'POWERS', 10);
  const pw = s.powers;
  const power = (kind: 'rapid' | 'spread' | 'pierce' | 'chrono' | 'shield', y: number, left: number, total: number): void => {
    const look = CAPSULE_LOOK[kind];
    const on = left > 0;
    if (on) drawCapsule(p, kind, 14, y + 4, s.tick);
    else p.box(8, y - 1, 12, 10, DARK_GREY);
    p.text(look.name, 28, y, on ? look.colour : DARK_GREY);
    if (on && total > 0) bar(p, y + 10, left / total, look.colour);
  };
  power('rapid', 96, pw.rapid, NOVA.timed);
  power('spread', 114, pw.spread, NOVA.timed);
  power('pierce', 132, pw.pierce, NOVA.timed);
  power('chrono', 150, pw.chrono, NOVA.chronoTicks);
  power('shield', 168, pw.shield ? 1 : 0, 0);
  title(p, 'NOVA', 24);
  for (let i = 0; i < NOVA.maxNovas; i++) {
    const x = MID - 20 + i * 20;
    if (i < pw.novas) drawCapsule(p, 'nova', x, 210, s.tick);
    else p.box(x - 6, 205, 12, 10, DARK_GREY);
  }
  title(p, 'NEXT SHIP', 30, GREY);
  p.text(String(s.nextLifeAt), MID, 250, SKY, 'center');
}

/** Classic's panel: the marksmanship the results will show, as it stands. */
function drawTally(p: Screen, s: GameState): void {
  ground(p, 'left');
  const { shots, hits } = s.stats;
  const figure = (label: string, value: string, row: number, colour: Colour): void => {
    title(p, label, row, RED);
    p.text(value, MID, (row + 2) * 8, colour, 'center');
  };
  figure('SHOTS', String(shots), 2, YELLOW);
  figure('HITS', String(hits), 7, YELLOW);
  figure('RATIO', shots > 0 ? `${((hits / shots) * 100).toFixed(1)} %` : '-', 12, WHITE);
  figure('NEXT SHIP', String(s.nextLifeAt), 19, SKY);
  title(p, 'AS IN 1981', 27, GREY);
  p.text('TWO SHOTS', MID, 228, DARK_GREY, 'center');
  p.text('NO POWERS', MID, 238, DARK_GREY, 'center');
}

/** The two panels beside a game in play. */
export function drawGamePanels(left: Screen, right: Screen, s: GameState, info: PanelInfo): void {
  drawCard(left, s, info);
  if (s.mode === 'supernova') drawGauges(right, s);
  else drawTally(right, s);
}
